import React from "react";
import { Moon, Sun, Crown, Tag, X } from "lucide-react";
import { Button } from "../../ui/button";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

/**
 * Single row inside the ChannelsTab list. Owns its own visual states
 * (active / paused / listed) and renders the appropriate action buttons.
 *
 * Props:
 *   ch                 — channel doc
 *   activeId           — id of currently-active channel (for highlight + "Act as" toggle)
 *   listing            — marketplace listing doc for this channel (or undefined)
 *   goDarkCostPerWeek  — for the "Dark renews" detail line
 *   onActivate         — () => parent activates this channel
 *   onResume           — () => parent resumes from pause
 *   onGoDark           — () => parent opens the pause panel
 *   onList             — () => parent opens the list panel
 *   onCancelListing    — () => parent cancels existing listing
 */
const ChannelRow = ({
  ch, activeId, listing, goDarkCostPerWeek,
  onActivate, onResume, onGoDark, onList, onCancelListing,
}) => {
  const isActive = ch.id === activeId;
  const wrapperCls = isActive
    ? "bg-emerald-500/5 border-emerald-500/40"
    : ch.is_paused
      ? "bg-amber-500/5 border-amber-500/30"
      : "bg-gray-950 border-gray-800";

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border ${wrapperCls}`}
      data-testid={`channel-row-${ch.username}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-emerald-400 text-sm">/{ch.username}</span>
          {ch.is_master && (
            <span className="text-[10px] uppercase tracking-wide text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 flex items-center gap-1">
              <Crown className="h-2.5 w-2.5" /> Master
            </span>
          )}
          {isActive && (
            <span className="text-[10px] uppercase tracking-wide text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
              Active
            </span>
          )}
          {ch.is_paused && (
            <span className="text-[10px] uppercase tracking-wide text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 flex items-center gap-1">
              <Moon className="h-2.5 w-2.5" /> Dark
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Created {fmtDate(ch.created_at)}</span>
          {!ch.is_master && ch.maint_next_charge_at && (
            <span>Next maint: {fmtDate(ch.maint_next_charge_at)}</span>
          )}
          {ch.is_paused && ch.paused_next_charge_at && (
            <span>Dark renews: {fmtDate(ch.paused_next_charge_at)} · {ch.paused_renewal_weeks}wk @ {ch.paused_renewal_weeks * goDarkCostPerWeek}cr</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0 mt-2 sm:mt-0">
        {!isActive && !ch.is_paused && (
          <Button
            size="sm" variant="outline"
            onClick={onActivate}
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs h-7"
            data-testid={`act-as-${ch.username}`}
          >
            Act as
          </Button>
        )}
        {ch.is_paused ? (
          <Button
            size="sm" variant="outline"
            onClick={onResume}
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs h-7"
            data-testid={`resume-${ch.username}`}
          >
            <Sun className="h-3 w-3 mr-1" /> Resume
          </Button>
        ) : (
          <Button
            size="sm" variant="outline"
            onClick={onGoDark}
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs h-7"
            data-testid={`go-dark-${ch.username}`}
          >
            <Moon className="h-3 w-3 mr-1" /> Go dark
          </Button>
        )}
        {/* Marketplace actions — never on master channels, never on paused
            channels. Existing listings show a Cancel button instead of a
            List button so the seller can yank without leaving the page. */}
        {!ch.is_master && !ch.is_paused && (
          listing ? (
            <Button
              size="sm" variant="outline"
              onClick={onCancelListing}
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs h-7"
              data-testid={`cancel-listing-${ch.username}`}
            >
              <X className="h-3 w-3 mr-1" />
              Listed @ {listing.price_credits.toLocaleString()} cr — cancel
            </Button>
          ) : (
            <Button
              size="sm" variant="outline"
              onClick={onList}
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-xs h-7"
              data-testid={`list-${ch.username}`}
            >
              <Tag className="h-3 w-3 mr-1" /> List for sale
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default ChannelRow;
